import { QueryKey } from '@tanstack/react-query';

type QueryKeys<D extends Record<string, _Desc>> = { [K in keyof D]: _ById<D[K]> & _List<D[K]> & _Base<D[K]> };
type _Desc<ID extends string = string, L extends boolean = boolean> = { id?: ID; list?: L };
type _ById<D extends _Desc> = D['id'] extends string
  ? { [ID in D['id'] as `by${Capitalize<D['id']>}`]: (id: number) => QueryKey }
  : {};
type _List<D extends _Desc> = D['list'] extends true ? { list: () => QueryKey } : {};
type _Base<D extends _Desc> = D['list'] extends true ? {} : D['id'] extends string ? {} : () => QueryKey;

/**
 * Builds a query key factory following the descriptors, providing clear API and autocompletion.
 */
export const getQueryKeys = <D extends Record<string, _Desc>>(map: D) =>
  (Object.keys(map) as (keyof D)[]).reduce((acc, name) => {
    const entry = map[name];
    const { id, list } = entry ?? {};
    const byIdKey = id ? `by${id[0]?.toUpperCase() ?? ''}${id.slice(1)}` : null;

    acc[name] = (
      !id && !list
        ? () => [name]
        : {
            ...(byIdKey ? { [byIdKey]: (arg: number) => [name, byIdKey, arg] } : {}),
            ...(list ? { list: () => [name, 'list'] } : {}),
          }
    ) as any;
    return acc;
  }, {} as QueryKeys<D>);

/**
 * @example
 *   useQuery({ queryKey: queryKeys.creativeTypes(), queryFn })
 *   queryClient.cancelQueries({ queryKey: queryKeys.campaign.byCampaignId(42), exact: true });
 *   queryKeys.campaign.list()
 */
export const queryKeys = getQueryKeys({
  campaign: { id: 'campaignId', list: true },
  creativeTypes: {},
  metadata: {},
  ga4Url: { id: 'campaignId' },
  ga4Info: { id: 'campaignId' },
  campaignStats: { id: 'campaignId' },
  campaignListStats: {},
  campaignReportingUrls: { id: 'campaignId' },
} as const);
